import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import {
    GiftCertificateProps,
    GiftCertificateFormData,
    GiftCertificateFormFields,
} from '../../Summary.definitions';
import { Form } from '~/shared/components/form/components/form/form';
import { useTranslation } from '~/shared/utils/translation';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { GiftCertificateFormElements } from './GiftCertificateFormElements';

const useValidationSchema = () => {
    const { translate } = useTranslation();

    return yup.object().shape({
        [GiftCertificateFormFields.Card]: yup
            .string()
            .required('')
            .matches(/^\d{19}$/, translate('checkout.giftCertificateForm.cardFormat')),
        [GiftCertificateFormFields.Cvc]: yup.string().matches(/^\d{3}$/, {
            message: translate('checkout.giftCertificateForm.cardFormat'),
        }),
    });
};

type Props = Pick<GiftCertificateProps, 'onSubmit' | 'name' | 'submitLabel'> & {
    onChange: (data: GiftCertificateFormData) => void;
    giftCardError?: string;
};

export const GiftCertificateForm = forwardRef(
    ({ onSubmit, submitLabel, onChange, giftCardError }: Props, ref) => {
        const validationSchema = useValidationSchema();

        const methods = useForm<GiftCertificateFormData>({
            mode: 'onChange',
            resolver: yupResolver(validationSchema),
        });

        const { watch, reset, clearErrors, formState } = methods;

        const cardValue = watch(GiftCertificateFormFields.Card);
        const cvcValue = watch(GiftCertificateFormFields.Cvc);

        useEffect(() => {
            onChange({
                card: cardValue ?? '',
                cvc: cvcValue ?? '',
            });
        }, [cardValue, cvcValue]);

        useImperativeHandle(ref, () => ({
            reset: () => {
                reset();
                clearErrors();
            },
        }));

        return (
            <Form methods={methods} onSubmit={onSubmit}>
                <GiftCertificateFormElements
                    giftCardError={giftCardError}
                    submitLabel={submitLabel}
                    isFormstateValid={formState.isValid}
                />
            </Form>
        );
    },
);

GiftCertificateForm.displayName = 'GiftCertificateForm';
